/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState } from 'react';
import {
  FormControl,
  FormLabel,
  SimpleGrid,
  GridItem,
  Input,
  InputRightAddon,
  Select,
  Stack,
  useShowMessage,
} from '../../core-ui';
import { validateAccount } from '../../support/safeValidators';

export type Account = {
  id: string;
  name: string;
  email: string;
  suspended: boolean;
  contactName: string;
  phoneNumber: string;
  subdomain: string;
  subdomainTitle: string;
  isSystemAdmin: boolean;
  showNewFeatures: boolean;
  achOnly: boolean;
};

type Props = {
  formId: string;
  account: Account;
  onSave: (account: Account) => void;
  children?: React.ReactNode;
};

export default function AccountEditForm(props: Props) {
  const { formId, account, onSave, children } = props;

  const [organizationName, setOrganizationName] = useState(account.name);
  const [contactName, setContactName] = useState(account.contactName ?? '');
  const [phoneNumber, setPhoneNumber] = useState(account.phoneNumber ?? '');
  const [contactEmail, setContactEmail] = useState(account.email);
  const [isSystemAdmin, setIsSystemAdmin] = useState(
    account.isSystemAdmin ?? false,
  );
  const [suspended, setSuspended] = useState(account.suspended ?? false);
  const [subdomain, setSubdomain] = useState(account.subdomain ?? '');
  const [showNewFeatures, setShowNewFeatures] = useState(
    account.showNewFeatures ?? false,
  );
  const [achOnly, setAchOnly] = useState(account.achOnly ?? false);

  const showMessage = useShowMessage();

  const onSubmit = async () => {
    const newAccount: Account = {
      id: account.id,
      name: organizationName,
      contactName: contactName,
      phoneNumber: phoneNumber,
      email: contactEmail,
      isSystemAdmin: isSystemAdmin,
      suspended: suspended,
      showNewFeatures: showNewFeatures,
      achOnly: achOnly,
      subdomain: subdomain,
      subdomainTitle: organizationName,
    };
    const { isValid, reason } = validateAccount(newAccount);
    if (!isValid) {
      showMessage('error', reason);
      return;
    }
    onSave(newAccount);
  };

  return (
    <SimpleGrid
      as="form"
      id={formId}
      columns={2}
      gap={2}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <GridItem colSpan={1}>
        <FormControl>
          <FormLabel>{t('Organization Name')}</FormLabel>
          <Input
            onChange={(event) => setOrganizationName(event.target.value)}
            value={organizationName}
          />
        </FormControl>
      </GridItem>
      <GridItem colSpan={1}>
        <FormControl>
          <FormLabel>{t('Subdomain')}</FormLabel>
          <Stack direction="row" spacing={2}>
            <Input
              onChange={(event) => setSubdomain(event.target.value)}
              value={subdomain}
            />
            <InputRightAddon children=".mymarket.org" />
          </Stack>
        </FormControl>
      </GridItem>
      <GridItem colSpan={1}>
        <FormControl>
          <FormLabel>{t('Contact Name')}</FormLabel>
          <Input
            onChange={(event) => setContactName(event.target.value)}
            value={contactName}
          />
        </FormControl>
      </GridItem>
      <GridItem colSpan={1}>
        <FormControl>
          <FormLabel>{t('System Administrator')}</FormLabel>
          <Select
            options={[true, false]}
            value={isSystemAdmin}
            getLabel={(value) => (value ? t('Yes') : t('No'))}
            onChange={(value) => setIsSystemAdmin(value)}
          />
        </FormControl>
      </GridItem>

      <GridItem colSpan={1}>
        <FormControl>
          <FormLabel>{t('Contact Email')}</FormLabel>
          <Input
            onChange={(event) => setContactEmail(event.target.value)}
            value={contactEmail}
          />
        </FormControl>
      </GridItem>
      <GridItem colSpan={1}>
        <FormControl>
          <FormLabel>{t('Suspend Account')}</FormLabel>
          <Select
            options={[true, false]}
            value={suspended}
            getLabel={(value) => (value ? 'Yes' : 'No')}
            onChange={(value) => setSuspended(value)}
          />
        </FormControl>
      </GridItem>
      <GridItem colSpan={1}>
        <FormControl>
          <FormLabel>{t('Contact Phone Number (optional)')}</FormLabel>
          <Input
            onChange={(event) => setPhoneNumber(event.target.value)}
            value={phoneNumber}
          />
        </FormControl>
      </GridItem>
      <GridItem colSpan={1}>
        <FormControl>
          <FormLabel>{t('Show New Features')}</FormLabel>
          <Select
            options={[true, false]}
            value={showNewFeatures}
            getLabel={(value) => (value ? 'Yes' : 'No')}
            onChange={(value) => setShowNewFeatures(value)}
          />
        </FormControl>
      </GridItem>
      <GridItem colSpan={1}>
        <FormControl>
          <FormLabel>{t('ACH payments only')}</FormLabel>
          <Select
            options={[true, false]}
            value={achOnly}
            getLabel={(value) => (value ? 'Yes' : 'No')}
            onChange={(value) => setAchOnly(value)}
          />
        </FormControl>
      </GridItem>

      {children != null ? <GridItem colSpan={2}>{children}</GridItem> : null}
    </SimpleGrid>
  );
}
